import { gql } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { apolloClient } from "../../..";
import closeIcon from "../../../assets/close.svg";
import { notify } from "../../Notify/Notify";
import CategorySelector from "../CategorySelector/CategorySelector";
import TagsInput from "../TagsInput/TagsInput";

// Styles
import "./AddEditTagModal.scss";

const AddEditTagModal = ({
  isDocumentView,
  isModalOpen,
  setIsModalOpen,
  listTags,
  setListTags,
  listCategories,
  setListCategories,
  prevSelectedCategory,
  selectedTag,
  loadTags,
}) => {
  const [tagList, setTagList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCategory, setNewCategory] = useState(null);
  const [areAllTagsValid, setAreAllTagsValid] = useState(true);
  const [singleTagString, setSingleTagString] = useState("");
  const [isReplaceOldCategory, setIsReplaceOldCategory] = useState(false);

  useEffect(() => {
    if (selectedTag) {
      setSelectedCategory(selectedTag.category);
      setSingleTagString(selectedTag.name);
    }
  }, [selectedTag]);

  // Reset states
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
    setNewCategory(null);
    setAreAllTagsValid(true);
  };

  const handleAddTags = async (tags, category) => {
    // If already existing category is selected
    if (category.id) {
      addTags(tags, category.id);
    } else {
      try {
        category = await addCategory(category);
        if (category) {
          addTags(tags, category.id);
        }
      } catch (e) {
        const title = "Error";
        const detail = e.message ?? "oops! Something went wrong";
        const type = "error";
        notify(title, detail, type);
        setIsModalOpen(false);
      }
    }
  };

  const handleEditTag = async (tagString, selectedTag, category, isReplaceOldCategory) => {
    // If already existing category is selected
    if (category.id) {
      editTag(tagString, selectedTag, category.id, isReplaceOldCategory);
    } else {
      try {
        category = await addCategory(category);
        if (category) {
          editTag(tagString, selectedTag, category.id, isReplaceOldCategory);
        }
      } catch (e) {
        const title = "Error";
        const detail = e.message ?? "oops! Something went wrong";
        const type = "error";
        notify(title, detail, type);
        setIsModalOpen(false);
      }
    }
  };

  const addCategory = async (category) => {
    try {
      // Create Category
      const variables = {
        categoryName: category.name,
      };
      const result = await apolloClient.mutate({
        variables: variables,
        mutation: gql`
          mutation AddCategory($categoryName: String!) {
            addCategory(categoryName: $categoryName) {
              success
              category {
                id
                name
              }
            }
          }
        `,
      });
      const { success } = result.data.addCategory;
      if (success) {
        setListCategories((listCategories) => [...listCategories, result.data.addCategory.category]);
        return result.data.addCategory.category;
      }
    } catch (error) {
      throw error;
    }
  };

  const addTags = async (tags, categoryId) => {
    try {
      // Add tag
      const variables = {
        tagNames: tags,
        categoryId: categoryId,
      };
      const result = await apolloClient.mutate({
        variables: variables,
        mutation: gql`
          mutation AddTags($tagNames: [String]!, $categoryId: UUID!) {
            addTags(tagNames: $tagNames, categoryId: $categoryId) {
              success
              tags {
                id
                name
                isActive
                createdTs
                modifiedTs
                category {
                  id
                  name
                }
              }
            }
          }
        `,
      });
      if (result.data.addTags.success) {
        // Update the tag list
        setListTags([...listTags, ...result.data.addTags.tags]);
        // Show success message
        const title = "Success";
        const detail = "Tags added successfully!";
        const type = "success";
        notify(title, detail, type);
      }
    } catch (e) {
      const title = "Error";
      const detail = e.message ?? "oops! Something went wrong";
      const type = "error";
      notify(title, detail, type);
    } finally {
      setIsModalOpen(false);
    }
  };

  const editTag = async (tagString, selectedTag, categoryId, replaceOldCategory) => {
    try {
      // Edit tag
      const variables = {
        tagName: tagString,
        tagId: selectedTag.id,
        replaceOldCategory: replaceOldCategory,
        categoryId: categoryId,
      };
      const result = await apolloClient.mutate({
        variables: variables,
        mutation: gql`
          mutation EditTag($tagId: UUID!, $replaceOldCategory: Boolean!, $tagName: String!, $categoryId: UUID!) {
            editTag(
              tagId: $tagId
              replaceOldCategory: $replaceOldCategory
              tagName: $tagName
              categoryId: $categoryId
            ) {
              success
            }
          }
        `,
      });
      if (result.data.editTag.success) {
        // Update the tag list
        loadTags();

        // Show success message
        const title = "Success";
        const detail = `${replaceOldCategory ? "Tags" : "Tag"} updated successfully!`;
        const type = "success";
        notify(title, detail, type);
      }
    } catch (e) {
      const title = "Error";
      const detail = e.message ?? "oops! Something went wrong";
      const type = "error";
      notify(title, detail, type);
    } finally {
      setIsModalOpen(false);
    }
  };

  const handleSaveClick = () => {
    const category = newCategory ? { name: newCategory } : selectedCategory;
    if (selectedTag) {
      handleEditTag(singleTagString, selectedTag, category, isReplaceOldCategory);
    } else {
      let tags = tagList
      // If document view we just have 1 tag
      if(isDocumentView) {
        tags = [singleTagString.trim()]
      }
      handleAddTags(tags, category);
    }
  };

  return (
    <div className="add-tag-button-container">
      {!isDocumentView ? (
        <button
          className="btn-primary"
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          Add tag
        </button>
      ) : null}

      <Modal className="tag-modal" size="sm" centered show={isModalOpen} onHide={handleModalClose}>
        <div className="tag-modal-content">
          <div className="header">
            <span className="title">{selectedTag ? `Edit` : `Add`} Tag</span>
            <img className="close" src={closeIcon} alt="close" onClick={handleModalClose} />
          </div>

          <div className="body">
            <TagsInput
              {...{
                isDocumentView,
                listTags,
                areAllTagsValid,
                setAreAllTagsValid,
                setTagList,
                tagList,
                selectedTag,
                singleTagString,
                setSingleTagString,
              }}
            />
            <CategorySelector
              {...{
                isModalOpen,
                listCategories,
                selectedCategory,
                setSelectedCategory,
                newCategory,
                setNewCategory,
                selectedTag,
                isReplaceOldCategory,
                setIsReplaceOldCategory,
              }}
            />
          </div>

          <div className="footer">
            <button className="btn-secondary cancel-button" onClick={handleModalClose}>
              Cancel
            </button>
            <button
              className="btn-primary create-button"
              disabled={
                !areAllTagsValid ||
                (selectedTag && singleTagString.trim().length === 0) ||
                (!isDocumentView && !selectedTag && tagList.length === 0) ||
                (selectedCategory === null && newCategory === null)
              }
              onClick={handleSaveClick}
            >
              {selectedTag ? "Done" : "Add"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddEditTagModal;
